import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN } from '@pxl/models/common';
import {
    Article,
    ArticleChapter,
    ArticleImage,
    ArticlePreview,
    ArticleSearchResult,
    ArticleTag,
    ArticleTagType,
    ArticleTemplateType,
    articleTemplateTypes,
    ArticleText,
    KnowledgeDbService,
    LoggingService,
    LoginService,
} from '@pxl/models/contracts';
import {
    KnowledgeBaseSettingsModel,
    KnowledgeDBSearchAllAndPreloadResponse,
    KnowledgeDBService as KnowledgeDBBackendService,
    LocalizationModel,
    PublishedArticleModel,
    PublishedArticlePreviewModel,
    PublishedChapterModel,
    PublishedImageModel,
    PublishedTagModel,
    PublishedTextModel,
} from '@pxl/shared/generated';
import { LanguageService } from '@pxl/shared/translation';
import { firstValueFrom, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DefaultKnowledgeDbService implements KnowledgeDbService {
    private readonly apiVersion: string;
    private token: string;
    private readonly tags = new Map<string, ArticleTag[]>();
    private culture: string;

    public settings: KnowledgeBaseSettingsModel = null;

    public onPictureQualityChanged$ = new Subject<number>();
    public onCultureChanged$ = new Subject<string>();
    public onSettingsChanged$ = new Subject<KnowledgeBaseSettingsModel>();

    private readonly loggingService = inject(LoggingService);

    private readonly knowledgeDBBackendService = inject(KnowledgeDBBackendService);
    private readonly languageService = inject(LanguageService);
    private readonly loginService = inject(LoginService);
    private readonly config = inject(CONFIG_TOKEN);

    public constructor() {
        this.apiVersion = this.config.API_VERSION;
        this.token = this.loginService.getToken();

        this.loginService.onUserLoginChanged().subscribe((loginState) => {
            this.token = loginState.isLoggedIn && loginState.isTermsOfUseForUserValid ? this.loginService.getToken() : null;
        });
    }

    public get selectedKnowledgeDbCulture(): string {
        return this.culture;
    }

    private updateCulture(culture: string) {
        this.culture =
            this.supportedKnowledgeDbCultures?.find((supportedCulture) => supportedCulture.culture === culture)?.culture ??
            this.fallbackCulture.culture;
        this.onCultureChanged$.next(this.culture);
    }

    private get supportedKnowledgeDbCultures(): LocalizationModel[] {
        return this.settings.localizationOptions;
    }

    public get fallbackCulture(): LocalizationModel {
        return this.settings.defaultLocalization;
    }

    public async refreshSettings(): Promise<KnowledgeBaseSettingsModel> {
        // Call APIs.
        try {
            this.settings = await firstValueFrom(this.knowledgeDBBackendService.getKnowledgeBaseSettings(this.apiVersion, this.token));
        } catch (error) {
            if (!this.settings) {
                // this ain't recoverable
                throw error;
            }
            this.loggingService.error('knowledgeDBBackendService.getKnowledgeBaseSettings', error);
        }

        // address the article cultures.
        this.updateCulture(this.languageService.getCulture());
        this.languageService.onCultureChange().subscribe((culture) => {
            this.updateCulture(culture);
        });

        // Load the rest of the settings.
        this.onSettingsChanged$.next(this.settings);

        const oldImageQuality = this.settings ? this.imageQuality : null;
        if (oldImageQuality !== this.imageQuality) {
            this.onPictureQualityChanged$.next(this.imageQuality);
        }

        return this.settings;
    }

    public get imageQuality(): number {
        return this.settings.customImageQuality ?? this.settings.defaultImageQuality;
    }

    public get numberOfArticlesToPreload(): number {
        return this.settings?.numberOfArticlesToPreload ?? 10;
    }

    public async search(fullText: string = null, tags: string[] = null): Promise<ArticleSearchResult> {
        const response = await this.loggingService.promiseTime(
            'DefaultKnowledgeDbService::search',
            firstValueFrom(
                this.knowledgeDBBackendService.searchAllAndPreload(
                    {
                        fullText,
                        tags,
                        numberOfArticlesToPreload: this.numberOfArticlesToPreload,
                    },
                    this.apiVersion,
                    this.token,
                ),
            ),
        );

        return await this.convertToArticleSearchResult(response);
    }

    public async getArticlePreviews(articleIds: string[]): Promise<ArticlePreview[]> {
        const localizedTags = await this.getTagsAsync();
        const articles = await this.loggingService.promiseTime(
            'DefaultKnowledgeDbService::getArticlePreviews',
            firstValueFrom(this.knowledgeDBBackendService.getArticlePreviews(articleIds, this.apiVersion, this.token)),
        );
        return articles.map((article) => this.convertArticlePreview(article, localizedTags));
    }

    public async getArticle(articleId: string): Promise<Article> {
        const localizedTags = await this.getTagsAsync();
        const article = await this.loggingService.promiseTime(
            'DefaultKnowledgeDbService::getArticle',
            firstValueFrom(this.knowledgeDBBackendService.getArticle(articleId, this.apiVersion, this.token)),
        );

        return this.convertArticle(article, localizedTags);
    }

    public addFavoriteAsync(articleId: string): Promise<void> {
        return firstValueFrom(this.knowledgeDBBackendService.addFavorite(articleId, this.apiVersion, this.token));
    }

    public removeFavoriteAsync(articleId: string): Promise<void> {
        return firstValueFrom(this.knowledgeDBBackendService.removeFavorite(articleId, this.apiVersion, this.token));
    }

    public addLikeAsync(articleId: string): Promise<void> {
        return firstValueFrom(this.knowledgeDBBackendService.addLike(articleId, this.apiVersion, this.token));
    }

    public removeLikeAsync(articleId: string): Promise<void> {
        return firstValueFrom(this.knowledgeDBBackendService.removeLike(articleId, this.apiVersion, this.token));
    }

    public async getFavoritesAsync(): Promise<ArticleSearchResult> {
        const response = await this.loggingService.promiseTime(
            'DefaultKnowledgeDbService::getFavoritesAsync',
            firstValueFrom(this.knowledgeDBBackendService.getFavoritesPreview(this.apiVersion, this.token)),
        );

        return await this.convertToArticleSearchResult(response);
    }

    public async getTagsAsync(): Promise<ArticleTag[]> {
        if (!this.tags.has(this.culture)) {
            const tags = await firstValueFrom(this.knowledgeDBBackendService.getTags(this.apiVersion, this.token));
            const convertedTags = this.convertTags(tags);
            if (!convertedTags.some((t) => t.identifier.includes('author')) && this.tags.has(this.fallbackCulture.culture)) {
                const authorTag = this.tags.get(this.fallbackCulture.culture).find((t) => t.identifier.includes('author'));
                convertedTags.push(authorTag);
            }
            this.tags.set(this.culture, convertedTags);
        }
        return this.tags.get(this.culture);
    }

    private static getText(val: { text: string }) {
        return val ? val.text : null;
    }

    private static convertImage(imageModel: PublishedImageModel): ArticleImage {
        if (!imageModel) {
            return null;
        }

        const { altText, baseUri, caption } = imageModel;

        return {
            baseUri: baseUri,
            caption: DefaultKnowledgeDbService.getText(caption),
            altText: DefaultKnowledgeDbService.getText(altText),
        };
    }

    private static convertImages(imageModels: PublishedImageModel[]): ArticleImage[] {
        return imageModels
            .filter((image) => !!image)
            .map((image) => {
                return DefaultKnowledgeDbService.convertImage(image);
            });
    }

    private static convertTexts(articleTexts: PublishedTextModel[]): ArticleText[] {
        return articleTexts.map((articleText) => {
            return {
                text: articleText.text,
                styles: [''],
            };
        });
    }

    private static convertChapters(chapterModels: PublishedChapterModel[]): ArticleChapter[] {
        return chapterModels.map((chapter) => {
            const { headline, images, paragraphs, template } = chapter;
            const articleTemplate = template ? articleTemplateTypes[template] : articleTemplateTypes[ArticleTemplateType.Type1];

            return {
                headline: DefaultKnowledgeDbService.getText(headline),
                images: DefaultKnowledgeDbService.convertImages(images),
                paragraphs: DefaultKnowledgeDbService.convertTexts(paragraphs),
                template: articleTemplate,
            };
        });
    }

    private convertTags(tagModels: PublishedTagModel[]): ArticleTag[] {
        return (
            tagModels
                .filter((t) => !!t.tag && !!t.displayText && !!t.displayText.text)
                // take only required tags.
                .filter(({ tag }) => ['specie', 'category', 'author', 'deepl'].some((type) => tag.includes(type)))
                // convert tag.
                .map((tagModel) => {
                    const tagIdentifier = tagModel.tag;
                    const isSpecie = tagIdentifier.includes('specie');
                    const isCategory = tagIdentifier.includes('category');
                    const isDeepl = tagIdentifier.includes('deepl');

                    return {
                        identifier: tagIdentifier,
                        text: DefaultKnowledgeDbService.getText(tagModel.displayText),
                        type: DefaultKnowledgeDbService.getArticleTagType(isSpecie, isCategory, isDeepl),
                    } as ArticleTag;
                })
        );
    }

    private static getArticleTagType(isSpecies: boolean, isCategory: boolean, isDeepl: boolean) {
        if (isSpecies) return ArticleTagType.Specie;
        if (isCategory) return ArticleTagType.Category;
        if (isDeepl) return ArticleTagType.Deepl;
        return ArticleTagType.Author;
    }

    private convertArticlePreview(articlePreview: PublishedArticlePreviewModel, localizedTags: ArticleTag[]): ArticlePreview {
        const {
            articleId,
            tags: tagIdentifiers,
            title,
            teaserText,
            teaserImage,
            isFavorite,
            bookmarkedUtc,
            timeStamp,
            isLiked: isLiked,
            likes: likes,
        } = articlePreview;

        const convertedImage: ArticleImage = DefaultKnowledgeDbService.convertImage(teaserImage);
        const convertedTeaserText: string = DefaultKnowledgeDbService.getText(teaserText);
        const convertedTitle: string = DefaultKnowledgeDbService.getText(title);

        const requiresDisclaimer = !tagIdentifiers.includes('no-disclaimer');

        const modTags = tagIdentifiers
            .map((tId) => localizedTags.find((tModel) => tModel.identifier === tId))
            .filter((t) => !!t)
            .sort((a, b) => {
                if (a.type === b.type) return 0;
                if (a.type === ArticleTagType.Specie) return -1;
                return 1;
            });

        return {
            articleId,
            tags: modTags.filter((tag) => tag.type !== ArticleTagType.Deepl),
            teaserImage: convertedImage,
            teaserText: convertedTeaserText,
            title: convertedTitle,
            isFavorite,
            isLiked: isLiked,
            requiresDisclaimer: requiresDisclaimer,
            isAutoTranslated: modTags.some((tag) => tag.type === ArticleTagType.Deepl && tag.identifier.includes(this.culture)),
            bookmarkedUtc: new Date(bookmarkedUtc),
            timeStamp: new Date(timeStamp),
            likes: likes,
        };
    }

    private convertArticle(article: PublishedArticleModel, localizedTags: ArticleTag[]): Article {
        const preview = this.convertArticlePreview(article, localizedTags);
        const chapters: ArticleChapter[] = DefaultKnowledgeDbService.convertChapters(article.chapters);

        return {
            ...preview,
            chapters,
        };
    }

    private async convertToArticleSearchResult(response: KnowledgeDBSearchAllAndPreloadResponse): Promise<ArticleSearchResult> {
        if (!response.preloadedArticles) {
            return new ArticleSearchResult(response.articleIds);
        }

        const localizedTags = await this.getTagsAsync();
        const preLoaded = response.preloadedArticles.map((article) => this.convertArticlePreview(article, localizedTags));

        return new ArticleSearchResult(response.articleIds, preLoaded);
    }
}
